import type { FieldValues, UseFormSetError } from 'react-hook-form';
import useIsStepByStepEnabled from '~/client/components/Auth/Authenticate/AuthSteps/hooks/useIsStepByStepEnabled';
import useCid from '~/client/hooks/cookies/marketing/useCid';
import useRefAff from '~/client/hooks/cookies/marketing/useRefAff';
import useRefCode from '~/client/hooks/cookies/marketing/useRefCode';
import useFriendRef from '~/client/hooks/cookies/useFriendRef';
import useSocialAuthType from '~/client/hooks/cookies/useSocialAuthType';
import useQueryParam from '~/client/hooks/router/useQueryParam';
import useHandleMetamaskAuthResponse from './helpers/handleMetamaskAuthResponse';
import useMetaMaskUserFastSignup from './useAuthMetamaskUserFastSignup';

type Args<T extends FieldValues> = {
  values: Omit<MetamaskDepositFormValues, 'dateOfBirth'> & {
    dateOfBirth?: number;
    accessCode?: string;
    ethAddress?: string;
  };
  setError?: UseFormSetError<T>;
  setErrors?: (errorsFragment: GenericErrorType[]) => void;
  setOtpError?: () => void;
  otp?: string;
  redirectUrl: string;
};

const useAuthMetamaskUser = () => {
  const isStepByStepEnabled = useIsStepByStepEnabled();
  const refCode = useRefCode();
  const [refAff] = useRefAff();
  const [friendRef] = useFriendRef();
  const [cid] = useCid();
  const [ethAddr] = useQueryParam('addr');
  const [, setSocialAuthType] = useSocialAuthType();
  const { handleMetamaskAuthFastSignup, loading } = useMetaMaskUserFastSignup();

  const { handleMetamaskAuthResponse } = useHandleMetamaskAuthResponse();

  const handleMetamaskAuth = async <T extends FieldValues>({
    values,
    setError,
    setErrors,
    setOtpError,
    otp,
    redirectUrl,
  }: Args<T>) => {
    setSocialAuthType('metamask');

    const { ethAddress: ethAccountAddress } = values;
    const address = ethAddr || ethAccountAddress || '';

    const path = isStepByStepEnabled
      ? '/authenticate'
      : '/complete-registration';

    const response = await handleMetamaskAuthFastSignup({
      address,
      otp,
      values,
      path,
      refCode,
      refAff,
      cid,
      redirectUrl,
      friendRef,
    });

    return handleMetamaskAuthResponse<T>({
      setError,
      setErrors,
      setOtpError,
      redirectUrl,
      data: response,
      address,
    });
  };

  return { loading, handleMetamaskAuth };
};

export default useAuthMetamaskUser;
